/*
 * SPEC-2 §7 / SPEC-METHODS §5.3 — the concurrency contract on the client side.
 *
 * Every PATCH below takes `version` as a REQUIRED field, not an optional one.
 * The server treats it as optional so pre-M1 clients keep working, but this app
 * is not one of them: a PATCH without a version is a silent last-writer-wins
 * overwrite, and making the field required at the type level means no screen
 * can forget it by accident.
 *
 * Every create sends an `Idempotency-Key`. On the routes the server dedupes
 * (methods, versions, applications, close-report, extract-method) a retry
 * returns the first attempt's entity; on the others the header is inert but
 * harmless, and sending it uniformly keeps the rule easy to state.
 */
import { api, fetchBlob, upload, type UploadProgress } from "@/api/client";
import type {
  AgentDetailOut,
  AgentDiffOut,
  AgentFeedbackOut,
  AgentLaunchOut,
  DeviceCommandOut,
  DeviceOut,
  DevicePairLookup,
  AgentRequestDecision,
  AgentRequestOut,
  AgentRunOut,
  AgentWorkItem,
  AgentWorkStats,
  AgentOut,
  AgentPatch,
  AgentVersionOut,
  AccessOut,
  ActivityOut,
  AgentConnectionOut,
  AgentStatsOut,
  AgentConnectionsOut,
  StatsPeriod,
  ConnectionDetailOut,
  ApplicationReportIn,
  AreaOut,
  AttachmentOut,
  AttachmentParentType,
  BusinessOut,
  CaseContentIn,
  CaseDetailOut,
  CaseOut,
  CasePlaceKind,
  CasePlaceOut,
  CaseStatus,
  CaseUseOut,
  CloseReportOut,
  CommentOut,
  CommentParentType,
  DelegationOut,
  HitOut,
  KnowledgeSourceOut,
  IdeaApproveResult,
  IdeaOut,
  IdeaSimilarityCard,
  MemoryStats,
  MethodApplicationOut,
  MethodDetailOut,
  MethodLinkKind,
  MethodLinkOut,
  MethodOut,
  MethodStatus,
  MethodVersionIn,
  MethodVersionOut,
  MyAccessOut,
  MySecret,
  NoteOut,
  OverviewOut,
  PassportRevisionOut,
  ProjectOut,
  ProjectStatusOut,
  ResourceKind,
  ResourceOut,
  RevisionField,
  SearchEntityType,
  SecretEventOut,
  SecretGrantOut,
  SecretOut,
  SecretsStatus,
  SecretValueOut,
  SimilarityCard,
  SuggestionOut,
  TaskHistoryOut,
  TaskLinkKind,
  TaskLinkOut,
  TaskLinksOut,
  TaskOut,
  TaskRevisionOut,
  TaskWorkLogEntryCreate,
  TaskWorkLogEntryOut,
  TerminalOutcome,
  ToolKind,
  ToolOut,
  ToolStats,
  UserOut,
  ProposalDiffOut,
} from "@/api/types";

export const Me = {
  get: () => api<UserOut>("/api/v1/me"),
  /** The person's own time zone; the notification settings follow it (PC audit, point 13). */
  update: (body: { timezone: string }) => api<UserOut>("/api/v1/me", { method: "PATCH", body }),
};

/**
 * The workspace's people. Needed wherever the API hands back a bare
 * `actor_id` / `author_id` and the UI has to show a name instead of a UUID
 * (comments, the partner-activity block).
 */
export const Users = {
  list: () => api<UserOut[]>("/api/v1/users"),
};

// FEAT-024: the shared activity log. `since` is an ISO timestamp.
export const Activity = {
  list: (params: { actor_id?: string; entity_type?: string; entity_id?: string; since?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params.actor_id) q.set("actor_id", params.actor_id);
    if (params.entity_type) q.set("entity_type", params.entity_type);
    if (params.entity_id) q.set("entity_id", params.entity_id);
    if (params.since) q.set("since", params.since);
    if (params.limit != null) q.set("limit", String(params.limit));
    return api<ActivityOut[]>(`/api/v1/activity?${q}`);
  },
};

/**
 * Spec 013 §8 — «Что происходит». Reading is side-effect free (agents use the
 * same endpoint); `seen` records that a person actually looked, so the next
 * "changes since last look" starts from now.
 */
export const Overview = {
  get: () => api<OverviewOut>("/api/v1/overview"),
  // `seen_at` is the snapshot the person actually saw, never "now".
  seen: (seen_at: string) =>
    api<{ seen_at: string }>("/api/v1/overview/seen", { method: "POST", body: { seen_at } }),
};

/**
 * Server-side similarity search (d5c / FEAT-022). Two paths, not one path with
 * a `kind` switch: the two return different card shapes, and the API keeps them
 * separate so neither client has to narrow a union.
 */
export const Search = {
  similarTasks: (q: string, params?: { scope?: "all" | "completed" | "active"; limit?: number }) =>
    api<SimilarityCard[]>(
      `/api/v1/search/similar?${new URLSearchParams({
        q,
        scope: params?.scope ?? "all",
        limit: String(params?.limit ?? 8),
      })}`,
    ),
  similarIdeas: (q: string, params?: { scope?: "all" | "open" | "rejected"; limit?: number }) =>
    api<IdeaSimilarityCard[]>(
      `/api/v1/search/similar-ideas?${new URLSearchParams({
        q,
        scope: params?.scope ?? "all",
        limit: String(params?.limit ?? 8),
      })}`,
    ),
};

// FEAT-021: files hanging off a task / idea / note.
export const Attachments = {
  list: (parent_type: AttachmentParentType, parent_id: string) =>
    api<AttachmentOut[]>(
      `/api/v1/attachments?${new URLSearchParams({ parent_type, parent_id })}`,
    ),
  /** Multipart upload with real byte progress — see `upload()` in client.ts. */
  create: (
    parent_type: AttachmentParentType,
    parent_id: string,
    file: File,
    onProgress?: (p: UploadProgress) => void,
  ) => {
    const form = new FormData();
    form.append("parent_type", parent_type);
    form.append("parent_id", parent_id);
    form.append("file", file);
    return upload<AttachmentOut>("/api/v1/attachments", form, onProgress);
  },
  /**
   * Pull the file down and hand it to the browser.
   *
   * Not a link: the API only accepts the token as a header, so an <a href>
   * would come back 401. The object URL is revoked on the next tick — long
   * enough for the click to be dispatched, short enough not to leak the blob.
   */
  download: async (id: string, filename: string) => {
    const blob = await fetchBlob(`/api/v1/attachments/${id}`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },
  remove: (id: string) => api<void>(`/api/v1/attachments/${id}`, { method: "DELETE" }),
};

export const Areas = {
  list: (include_archived = false) =>
    api<AreaOut[]>(`/api/v1/areas${include_archived ? "?include_archived=true" : ""}`),
  get: (id: string) => api<AreaOut>(`/api/v1/areas/${id}`),
  create: (body: {
    name: string;
    slug: string;
    description_md?: string;
    color?: string;
    icon?: string;
    order?: number;
  }) => api<AreaOut>("/api/v1/areas", { method: "POST", body }),
  update: (
    id: string,
    body: Partial<{
      name: string;
      description_md: string;
      color: string;
      icon: string;
      order: number;
      archived: boolean;
    }>,
  ) => api<AreaOut>(`/api/v1/areas/${id}`, { method: "PATCH", body }),
  archive: (id: string) => api<void>(`/api/v1/areas/${id}`, { method: "DELETE" }),
};

export const Projects = {
  list: (params?: { area_id?: string | "none" }) => {
    const q = new URLSearchParams();
    if (params?.area_id) q.set("area_id", params.area_id);
    const qs = q.toString();
    return api<ProjectOut[]>(`/api/v1/projects${qs ? `?${qs}` : ""}`);
  },
  get: (id: string) => api<ProjectOut>(`/api/v1/projects/${id}`),
  create: (body: {
    title: string;
    template?: string;
    description_md?: string;
    summary?: string | null;
    target_completion_at?: string | null;
    area_id?: string | null;
  }) => api<ProjectOut>("/api/v1/projects", { method: "POST", body, idempotent: true }),
  update: (
    id: string,
    body: Partial<{
      title: string;
      summary: string | null;
      description_md: string | null;
      target_completion_at: string | null;
      archived: boolean;
      /** Spec 018: where a new passport text comes from. */
      source_md: string | null;
    }> & { version: number },
  ) => api<ProjectOut>(`/api/v1/projects/${id}`, { method: "PATCH", body }),
  statuses: (id: string) => api<ProjectStatusOut[]>(`/api/v1/projects/${id}/statuses`),
  /** Spec 018: a person confirms the passport is current. */
  checkPassport: (id: string, version: number) =>
    api<ProjectOut>(`/api/v1/projects/${id}/passport/checked`, {
      method: "POST",
      body: { version },
    }),
  passportRevisions: (id: string) =>
    api<PassportRevisionOut[]>(`/api/v1/projects/${id}/passport/revisions`),
  assignArea: (id: string, area_id: string | null) =>
    api<ProjectOut>(`/api/v1/projects/${id}/area`, {
      method: "PATCH",
      body: { area_id },
    }),
};

/** Spec 018: the business passport — the first page of context. */
export const Business = {
  get: () => api<BusinessOut>("/api/v1/business"),
  update: (body: { body_md: string; version: number; source_md?: string | null }) =>
    api<BusinessOut>("/api/v1/business", { method: "PUT", body }),
  check: (version: number) =>
    api<BusinessOut>("/api/v1/business/checked", { method: "POST", body: { version } }),
  revisions: () => api<PassportRevisionOut[]>("/api/v1/business/revisions"),
  templates: () =>
    api<{ business: string; project: string }>("/api/v1/knowledge/templates"),
};

/**
 * Spec 018 part B: what the business uses and who may do what with it. A card
 * says where a secret lives and how to get it; the server refuses the secret.
 */
export const ResourceCards = {
  list: (project_id?: string) =>
    api<ResourceOut[]>(
      `/api/v1/resources${project_id ? `?project_id=${encodeURIComponent(project_id)}` : ""}`,
    ),
  create: (body: {
    kind: ResourceKind;
    name: string;
    purpose?: string | null;
    address?: string | null;
    instructions_md?: string | null;
    renew_at?: string | null;
    project_ids?: string[];
  }) => api<ResourceOut>("/api/v1/resources", { method: "POST", body, idempotent: true }),
  archive: (id: string) => api<void>(`/api/v1/resources/${id}`, { method: "DELETE" }),
  grant: (
    id: string,
    body: {
      user_id: string;
      actions_md: string;
      how_to_get_md?: string | null;
      revoke_md?: string | null;
      expires_at?: string | null;
    },
  ) =>
    api<AccessOut>(`/api/v1/resources/${id}/accesses`, { method: "POST", body, idempotent: true }),
  revoke: (id: string, access_id: string, version: number) =>
    api<AccessOut>(`/api/v1/resources/${id}/accesses/${access_id}/revoke`, {
      method: "POST",
      body: { version },
    }),
  /** A mistaken or duplicate record leaves the lists; the access is not revoked. */
  withdraw: (id: string, access_id: string, version: number, reason: string) =>
    api<AccessOut>(`/api/v1/resources/${id}/accesses/${access_id}/withdraw`, {
      method: "POST",
      body: { version, reason },
    }),
  mine: () => api<MyAccessOut[]>("/api/v1/me/accesses"),
};

/**
 * Spec 018 part C: the secrets store. A value travels only in `create` and
 * `rotate` (typed by a person) and in the answer to `reveal`. None of them uses
 * the automatic retry key: after a lost reply it keeps the request body — the
 * value — in memory for the resend. `create` takes the form's own key instead,
 * and nothing here keeps a body.
 */
export const Secrets = {
  status: () => api<SecretsStatus>("/api/v1/secrets/status"),
  list: (resource_id: string, include_inactive = false) =>
    api<SecretOut[]>(
      `/api/v1/resources/${resource_id}/secrets${include_inactive ? "?include_inactive=true" : ""}`,
    ),
  get: (id: string, include_inactive = false) =>
    api<SecretOut>(`/api/v1/secrets/${id}${include_inactive ? "?include_inactive=true" : ""}`),
  /** A person only. The same key on a resend returns the first secret, not a second one. */
  create: (
    resource_id: string,
    body: { name: string; description_md?: string | null; value: string },
    idempotencyKey: string,
  ) =>
    api<SecretOut>(`/api/v1/resources/${resource_id}/secrets`, {
      method: "POST",
      body,
      idempotent: idempotencyKey,
    }),
  update: (
    id: string,
    body: Partial<{ name: string; description_md: string | null }> & { version: number },
  ) => api<SecretOut>(`/api/v1/secrets/${id}`, { method: "PATCH", body }),
  rotate: (id: string, value: string, version: number) =>
    api<SecretOut>(`/api/v1/secrets/${id}/rotate`, { method: "POST", body: { value, version } }),
  /** Irreversible: the value, grants and links go; the name and the audit stay. */
  destroy: (id: string, version: number) =>
    api<SecretOut>(`/api/v1/secrets/${id}/destroy`, { method: "POST", body: { version } }),
  /** Written to the audit before the value is sent. The caller keeps it in component state only. */
  reveal: (id: string) => api<SecretValueOut>(`/api/v1/secrets/${id}/reveal`, { method: "POST" }),
  /** No `token_id`: the person sees it in the Mini App; with one: only that agent connection. */
  grant: (
    id: string,
    body: { user_id: string; token_id?: string | null; expires_at?: string | null },
  ) =>
    api<SecretGrantOut>(`/api/v1/secrets/${id}/grants`, { method: "POST", body, idempotent: true }),
  revoke: (id: string, grant_id: string, version: number) =>
    api<SecretGrantOut>(`/api/v1/secrets/${id}/grants/${grant_id}/revoke`, {
      method: "POST",
      body: { version },
    }),
  events: (id: string, limit = 100) =>
    api<SecretEventOut[]>(`/api/v1/secrets/${id}/events?limit=${limit}`),
  mine: () => api<MySecret[]>("/api/v1/me/secrets"),
};

/** Spec 023: who did what (activity) and what agents reported spending (self-report). */
export const Stats = {
  agents: (period: StatsPeriod) => api<AgentStatsOut>(`/api/v1/stats/agents?period=${period}`),
};

/** The signed-in person's agent connections, as on «Подключения агентов». */
export const AgentConnections = {
  list: () => api<AgentConnectionsOut>("/api/v1/connections"),
  /** Task 46fcee9a: its rights on tasks, the secrets handed to it, its last action. */
  get: (id: string) => api<ConnectionDetailOut>(`/api/v1/connections/${id}`),
  /** Rename, or narrow the scope; widening answers 422 — it needs a new sign-in. */
  update: (id: string, body: { name?: string; scope?: ("read" | "write")[] }) =>
    api<AgentConnectionOut>(`/api/v1/connections/${id}`, { method: "PATCH", body }),
  disconnect: (id: string) => api<void>(`/api/v1/connections/${id}`, { method: "DELETE" }),
};

export const Tasks = {
  /** Spec 018: to another project, keeping ID, people, discussion and history. */
  move: (id: string, project_id: string, version: number) =>
    api<TaskOut>(`/api/v1/tasks/${id}/move`, { method: "POST", body: { project_id, version } }),
  listInProject: (project_id: string, params?: { assignee_id?: string }) => {
    const q = new URLSearchParams();
    if (params?.assignee_id) q.set("assignee_id", params.assignee_id);
    const qs = q.toString();
    return api<TaskOut[]>(`/api/v1/projects/${project_id}/tasks${qs ? `?${qs}` : ""}`);
  },
  get: (id: string) => api<TaskOut>(`/api/v1/tasks/${id}`),
  claim: (id: string) => api<TaskOut>(`/api/v1/tasks/${id}/claim`, { method: "POST" }),
  assign: (id: string, assignee_id: string | null, version: number) =>
    api<TaskOut>(`/api/v1/tasks/${id}/assignment`, { method: "PUT", body: { assignee_id, version } }),
  create: (body: {
    project_id: string;
    title: string;
    summary?: string | null;
    description_md?: string;
    due_at?: string;
    tags?: string[];
  }) => api<TaskOut>("/api/v1/tasks", { method: "POST", body, idempotent: true }),
  update: (
    id: string,
    body: Partial<{
      title: string;
      summary: string | null;
      description_md: string;
      status_id: string;
      due_at: string | null;
      completion_note_md: string | null;
    }> & { version: number },
  ) => api<TaskOut>(`/api/v1/tasks/${id}`, { method: "PATCH", body, idempotent: true }),
  /** Spec 014: numbered revisions of the description, title and due date. */
  history: (id: string, params: { field?: RevisionField; with_text?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.field) q.set("field", params.field);
    if (params.with_text) q.set("with_text", "true");
    const qs = q.toString();
    return api<TaskHistoryOut>(`/api/v1/tasks/${id}/history${qs ? `?${qs}` : ""}`);
  },
  /** A person erases a revision's text (a secret pasted by mistake). */
  redact: (id: string, revisionId: string) =>
    api<TaskRevisionOut>(`/api/v1/tasks/${id}/history/${revisionId}/redact`, { method: "POST" }),
  setTools: (id: string, body: { tool_slugs?: string[]; tool_ids?: string[] }) =>
    api<TaskOut>(`/api/v1/tasks/${id}/tools`, { method: "PUT", body }),
  remove: (id: string) => api<void>(`/api/v1/tasks/${id}`, { method: "DELETE" }),
  listActive: (limit = 50) => api<TaskOut[]>(`/api/v1/tasks/active?limit=${limit}`),
  listCompleted: (limit = 50) => api<TaskOut[]>(`/api/v1/tasks/recent-completed?limit=${limit}`),
  // FIX-027: the rows behind memory.stats.stale_in_progress. Same predicate as
  // the count server-side, so the list always agrees with the number that
  // linked to it.
  listStale: (days = 7, limit = 100) =>
    api<TaskOut[]>(`/api/v1/tasks/stale?days=${days}&limit=${limit}`),
  listJournal: (id: string, limit = 200) =>
    api<TaskWorkLogEntryOut[]>(`/api/v1/tasks/${id}/journal?limit=${limit}`),
  appendJournalEntry: (id: string, body: TaskWorkLogEntryCreate) =>
    api<TaskWorkLogEntryOut>(`/api/v1/tasks/${id}/journal`, { method: "POST", body }),
  // FEAT-024: back into work. The completion note is preserved server-side —
  // it records how the task was closed last time, and the next close replaces it.
  reopen: (id: string) => api<TaskOut>(`/api/v1/tasks/${id}/reopen`, { method: "POST" }),
};

// FEAT-024: discussion on a task / idea / note.
/** Spec 016: an edit sends only what changed; `summary_md: null` clears the short version. */
export type CommentPatch = { body_md?: string; summary_md?: string | null };

export const Comments = {
  list: (parent_type: CommentParentType, parent_id: string) =>
    api<CommentOut[]>(
      `/api/v1/comments?${new URLSearchParams({ parent_type, parent_id })}`,
    ),
  create: (body: { parent_type: CommentParentType; parent_id: string; body_md: string }) =>
    api<CommentOut>("/api/v1/comments", { method: "POST", body }),
  update: (id: string, body: CommentPatch) =>
    api<CommentOut>(`/api/v1/comments/${id}`, { method: "PATCH", body }),
  remove: (id: string) => api<void>(`/api/v1/comments/${id}`, { method: "DELETE" }),
};

// FEAT-023: memory-debt counters for the Home screen.
export const Memory = {
  stats: () => api<MemoryStats>("/api/v1/memory/stats"),
};

export const Ideas = {
  linkTask: (id: string, taskId: string, version: number) =>
    api(`/api/v1/ideas/${id}/realize`, {
      method: "POST", body: { version, existing_task_ids: [taskId] }, idempotent: true,
    }),
  list: (status?: string) => api<IdeaOut[]>(`/api/v1/ideas${status ? `?status=${status}` : ""}`),
  get: (id: string) => api<IdeaOut>(`/api/v1/ideas/${id}`),
  /** The idea's data without starting the partner's review (Spec 015). */
  peek: (id: string) => api<IdeaOut>(`/api/v1/ideas/${id}?peek=true`),
  create: (body: { original_text: string; summary?: string | null; body_md?: string }) =>
    api<IdeaOut>("/api/v1/ideas", { method: "POST", body, idempotent: true }),
  update: (
    id: string,
    body: Partial<{ summary: string | null; body_md: string | null; project_id: string | null }> & {
      version: number;
    },
  ) => api<IdeaOut>(`/api/v1/ideas/${id}`, { method: "PATCH", body }),
  approve: (id: string, body: { project_id?: string; status_id?: string; version?: number } = {}) =>
    api<IdeaApproveResult>(`/api/v1/ideas/${id}/approve`, { method: "POST", body }),
  reject: (id: string, comment_md: string) =>
    api<IdeaOut>(`/api/v1/ideas/${id}/reject`, { method: "POST", body: { comment_md } }),
  withdraw: (id: string, comment_md: string, version: number) =>
    api<IdeaOut>(`/api/v1/ideas/${id}/withdraw`, { method: "POST", body: { comment_md, version }, idempotent: true }),
  /** Spec 015: back to the discussion with a reason; the partner reviews it again. */
  reopen: (id: string, comment_md: string, version: number) =>
    api<IdeaOut>(`/api/v1/ideas/${id}/reopen`, { method: "POST", body: { comment_md, version }, idempotent: true }),
};

export const Tools = {
  list: (params?: { area_id?: string; kind?: ToolKind; include_archived?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.area_id) q.set("area_id", params.area_id);
    if (params?.kind) q.set("kind", params.kind);
    if (params?.include_archived) q.set("include_archived", "true");
    const qs = q.toString();
    return api<ToolOut[]>(`/api/v1/tools${qs ? `?${qs}` : ""}`);
  },
  // FEAT-025: usage figures, busiest first. A separate call from list() — same
  // registry, different row shape — and unused tools come back with zeros
  // rather than being omitted.
  stats: (include_archived = false) =>
    api<ToolStats[]>(`/api/v1/tools/stats${include_archived ? "?include_archived=true" : ""}`),
  get: (id: string) => api<ToolOut>(`/api/v1/tools/${id}`),
  create: (body: {
    name: string;
    slug: string;
    kind: ToolKind;
    repo_url: string;
    description_md?: string;
    homepage_url?: string;
    version?: string;
    tags?: string[];
    area_id?: string;
  }) => api<ToolOut>("/api/v1/tools", { method: "POST", body }),
  update: (
    id: string,
    body: Partial<{
      name: string;
      kind: ToolKind;
      description_md: string;
      repo_url: string;
      homepage_url: string;
      version: string;
      tags: string[];
      area_id: string | null;
      archived: boolean;
    }>,
  ) => api<ToolOut>(`/api/v1/tools/${id}`, { method: "PATCH", body }),
  archive: (id: string) => api<void>(`/api/v1/tools/${id}`, { method: "DELETE" }),
};

export const Notes = {
  list: () => api<NoteOut[]>("/api/v1/notes"),
  appendToday: (body_md: string) =>
    api<NoteOut>("/api/v1/notes/today/append", { method: "POST", body: { body_md } }),
  create: (body: { title?: string; body_md: string; tags?: string[]; is_shared?: boolean }) =>
    api<NoteOut>("/api/v1/notes", { method: "POST", body, idempotent: true }),
  update: (
    id: string,
    body: Partial<{ title: string | null; body_md: string; tags: string[] }> & { version: number },
  ) => api<NoteOut>(`/api/v1/notes/${id}`, { method: "PATCH", body }),
  get: (id: string) => api<NoteOut>(`/api/v1/notes/${id}`),
  promoteToTask: (id: string, project_id: string, title?: string) =>
    api<TaskOut>(`/api/v1/notes/${id}/promote-to-task`, { method: "POST", body: { project_id, title } }),
  promoteToIdea: (id: string) =>
    api<IdeaOut>(`/api/v1/notes/${id}/promote-to-idea`, { method: "POST" }),
};

/* ── M1–M3: methods, applications, delegation ─────────────────────────── */

/**
 * Every create here sends an `Idempotency-Key`, and the server honours it on
 * all of them. That matters more than usual for methods: a duplicated method
 * is worse than a duplicated task, because two half-written copies split the
 * evidence that would otherwise have proven one of them.
 */
export const Methods = {
  list: (params?: { status?: MethodStatus; area_id?: string; tag?: string; q?: string; limit?: number }) => {
    const p = new URLSearchParams();
    if (params?.status) p.set("status", params.status);
    if (params?.area_id) p.set("area_id", params.area_id);
    if (params?.tag) p.set("tag", params.tag);
    if (params?.q) p.set("q", params.q);
    if (params?.limit != null) p.set("limit", String(params.limit));
    const qs = p.toString();
    return api<MethodOut[]>(`/api/v1/methods${qs ? `?${qs}` : ""}`);
  },
  get: (id: string) => api<MethodDetailOut>(`/api/v1/methods/${id}`),
  create: (body: {
    title: string;
    summary?: string | null;
    area_id?: string | null;
    tags?: string[];
    first_version?: MethodVersionIn | null;
    source_idea_id?: string | null;
    task_id?: string | null;
  }) => api<MethodDetailOut>("/api/v1/methods", { method: "POST", body, idempotent: true }),
  /** `version` is the optimistic lock; the caller always sends what it read. */
  update: (
    id: string,
    body: Partial<{ title: string; summary: string | null; tags: string[]; area_id: string | null }> & {
      version: number;
    },
  ) => api<MethodDetailOut>(`/api/v1/methods/${id}`, { method: "PATCH", body }),
  addVersion: (
    id: string,
    body: { content: MethodVersionIn; change_note?: string | null; based_on_version_id?: string | null; set_current?: boolean },
  ) => api<MethodVersionOut>(`/api/v1/methods/${id}/versions`, { method: "POST", body, idempotent: true }),
  setCurrentVersion: (id: string, version_id: string, version: number) =>
    api<MethodDetailOut>(`/api/v1/methods/${id}/current-version`, {
      method: "POST",
      body: { version_id, version },
    }),
  setStatus: (id: string, status: MethodStatus, version: number) =>
    api<MethodDetailOut>(`/api/v1/methods/${id}/status`, { method: "POST", body: { status, version } }),
  applications: (id: string) => api<MethodApplicationOut[]>(`/api/v1/methods/${id}/applications`),
  addLink: (id: string, body: { kind: MethodLinkKind; target_id: string; label?: string | null }) =>
    api<MethodLinkOut>(`/api/v1/methods/${id}/links`, { method: "POST", body }),
  removeLink: (id: string, body: { kind: MethodLinkKind; target_id: string }) =>
    api<void>(`/api/v1/methods/${id}/links`, { method: "DELETE", body }),
};

export const Applications = {
  forTask: (task_id: string) => api<MethodApplicationOut[]>(`/api/v1/tasks/${task_id}/applications`),
  /** What a close report still owes an answer for. Empty means "close freely". */
  openForTask: (task_id: string) =>
    api<MethodApplicationOut[]>(`/api/v1/tasks/${task_id}/open-applications`),
  start: (task_id: string, body: { method_id: string; version_id?: string | null }) =>
    api<MethodApplicationOut>(`/api/v1/tasks/${task_id}/applications`, {
      method: "POST",
      body,
      idempotent: true,
    }),
  finish: (
    id: string,
    body: {
      outcome: TerminalOutcome;
      adaptations_md?: string | null;
      result_md?: string | null;
      limitations_md?: string | null;
      evidence_entry_ids?: string[];
      version: number;
    },
  ) => api<MethodApplicationOut>(`/api/v1/applications/${id}`, { method: "PATCH", body }),
};

/** Spec 017: links between tasks — «блокирует» and «связана с». */
export const TaskLinks = {
  list: (task_id: string) => api<TaskLinksOut>(`/api/v1/tasks/${task_id}/links`),
  add: (task_id: string, kind: TaskLinkKind, other_id: string) =>
    api<TaskLinkOut>(`/api/v1/tasks/${task_id}/links`, {
      method: "POST",
      body: { kind, task_id: other_id },
      idempotent: true,
    }),
  remove: (task_id: string, link_id: string) =>
    api<void>(`/api/v1/tasks/${task_id}/links/${link_id}`, { method: "DELETE" }),
};

export const Delegations = {
  list: (task_id: string, includeRevoked = false) =>
    api<DelegationOut[]>(`/api/v1/tasks/${task_id}/delegations${includeRevoked ? "?include_revoked=true" : ""}`),
  grant: (task_id: string, token_id: string, scope: string[]) =>
    api<DelegationOut>(`/api/v1/tasks/${task_id}/delegations`, {
      method: "POST",
      body: { token_id, scope },
    }),
  revoke: (task_id: string, token_id: string) =>
    api<void>(`/api/v1/tasks/${task_id}/delegations/${token_id}`, { method: "DELETE" }),
};

/**
 * The aggregate close (SPEC §2). One transaction: every open application
 * finishes, adaptations optionally become versions, the task closes. Either all
 * of it lands or none — which is why the UI never falls back to a plain PATCH
 * when this returns 422.
 */
export const CloseReport = {
  submit: (
    task_id: string,
    body: {
      completion_note_md?: string | null;
      version?: number;
      status_id?: string;
      close_task?: boolean;
      tool_slugs?: string[] | null;
      applications: ApplicationReportIn[];
    },
    idempotencyKey?: string,
  ) =>
    api<CloseReportOut>(`/api/v1/tasks/${task_id}/close-report`, {
      method: "POST",
      body,
      idempotent: idempotencyKey ?? true,
    }),
  extractMethod: (
    task_id: string,
    body: {
      entry_ids?: string[] | null;
      title?: string | null;
      summary?: string | null;
      body_md?: string | null;
    },
  ) =>
    api<MethodDetailOut>(`/api/v1/tasks/${task_id}/extract-method`, {
      method: "POST",
      body,
      idempotent: true,
    }),
};

/* ── M2: unified search and per-task suggestions ──────────────────────── */

export const Knowledge = {
  source: (type: string, id: string) =>
    api<KnowledgeSourceOut>(`/api/v1/knowledge/sources/${type}/${id}`),
  /**
   * One search across everything indexed. Matches paraphrases, so a miss here
   * is a real "we know of nothing like this" — unlike `Methods.list({q})`,
   * which is a substring match.
   */
  search: (q: string, params?: { types?: SearchEntityType[]; project_id?: string; limit?: number }) => {
    const p = new URLSearchParams({ q });
    if (params?.types?.length) p.set("types", params.types.join(","));
    if (params?.project_id) p.set("project_id", params.project_id);
    if (params?.limit != null) p.set("limit", String(params.limit));
    return api<HitOut[]>(`/api/v1/search?${p}`);
  },
  suggestions: (task_id: string, refresh = false) =>
    api<SuggestionOut[]>(
      `/api/v1/tasks/${task_id}/suggestions${refresh ? "?refresh=true" : ""}`,
    ),
  /** "Не подходит" — durable, and it survives every recomputation. */
  dismiss: (task_id: string, entity_type: SearchEntityType, entity_id: string) =>
    api<void>(`/api/v1/tasks/${task_id}/suggestions/${entity_type}/${entity_id}/dismiss`, {
      method: "POST",
    }),
};

/* ── Spec 025: cases, the places they go to, and each use ─────────────── */

/**
 * Every step of a case carries the version it was read at. People decide in the
 * app, so nothing here sends `user_confirmation` — that is how an agent passes
 * on a person's words.
 */
export const Cases = {
  /** No status: everything but the archive. `unused_at`: not yet used at that place. */
  list: (params: { status?: CaseStatus; unused_at?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.unused_at) q.set("unused_at", params.unused_at);
    const qs = q.toString();
    return api<CaseOut[]>(`/api/v1/cases${qs ? `?${qs}` : ""}`);
  },
  get: (id: string) => api<CaseDetailOut>(`/api/v1/cases/${id}`),
  /** The form's own key: a resend after a lost reply returns the first case, not a second one. */
  create: (body: CaseContentIn & { title: string }, idempotencyKey?: string) =>
    api<CaseDetailOut>("/api/v1/cases", { method: "POST", body, idempotent: idempotencyKey ?? true }),
  /** An edit of an accepted case sends it back to review; the answer says so. */
  update: (id: string, body: CaseContentIn & { version: number }) =>
    api<CaseDetailOut>(`/api/v1/cases/${id}`, { method: "PATCH", body }),
  /** Draft → review; refused while the problem or the solution is empty. */
  submit: (id: string, version: number) =>
    api<CaseDetailOut>(`/api/v1/cases/${id}/submit`, { method: "POST", body: { version }, idempotent: true }),
  accept: (id: string, version: number) =>
    api<CaseDetailOut>(`/api/v1/cases/${id}/accept`, { method: "POST", body: { version }, idempotent: true }),
  /**
   * `/return`: review → draft. The comment lands in the case discussion; it is
   * required for someone else's case, the author may pull theirs back without one.
   */
  returnToDraft: (id: string, version: number, comment_md?: string) =>
    api<CaseDetailOut>(`/api/v1/cases/${id}/return`, {
      method: "POST",
      body: comment_md ? { version, comment_md } : { version },
      idempotent: true,
    }),
  archive: (id: string, version: number) =>
    api<CaseDetailOut>(`/api/v1/cases/${id}/archive`, { method: "POST", body: { version }, idempotent: true }),
  /** Back from the archive as a draft. */
  restore: (id: string, version: number) =>
    api<CaseDetailOut>(`/api/v1/cases/${id}/restore`, { method: "POST", body: { version }, idempotent: true }),
  places: {
    list: (include_archived = false) =>
      api<CasePlaceOut[]>(`/api/v1/case-places${include_archived ? "?include_archived=true" : ""}`),
    /** 409 `place_exists` (the name in any case, or the same url) carries the place there is. */
    create: (body: { name: string; kind: CasePlaceKind; url?: string | null }) =>
      api<CasePlaceOut>("/api/v1/case-places", { method: "POST", body, idempotent: true }),
  },
  uses: {
    /** 409 `case_not_accepted`, or `case_already_used_here` with the earlier use. */
    create: (
      case_id: string,
      body: { place_id: string; url?: string | null; note?: string | null; used_at?: string | null },
    ) => api<CaseUseOut>(`/api/v1/cases/${case_id}/uses`, { method: "POST", body, idempotent: true }),
    /** People only: a use recorded by mistake. */
    remove: (case_id: string, use_id: string) =>
      api<void>(`/api/v1/cases/${case_id}/uses/${use_id}`, { method: "DELETE" }),
  },
};

/** Spec 027: agents' cards. Creating, approving, archiving and restoring are a person's. */
export const Agents = {
  list: (include_archived = false) =>
    api<AgentOut[]>(`/api/v1/agents${include_archived ? "?include_archived=true" : ""}`),
  get: (id: string) => api<AgentDetailOut>(`/api/v1/agents/${id}`),
  /** The form's own key: a resend after a lost reply returns the first agent, not a second one.
   * Spec 042: with `parent_id` — a helper of that staff member, in its unit with it. */
  create: (
    body: { name: string; purpose_md?: string | null; parent_id?: string | null; helper_mode?: "background" | "own_chat" | null },
    idempotencyKey?: string,
  ) => api<AgentDetailOut>("/api/v1/agents", { method: "POST", body, idempotent: idempotencyKey ?? true }),
  /** A change of the card is a new version; the name and the purpose write none. */
  update: (id: string, body: AgentPatch & { version: number }) =>
    api<AgentDetailOut>(`/api/v1/agents/${id}`, { method: "PATCH", body }),
  versions: (id: string) => api<AgentVersionOut[]>(`/api/v1/agents/${id}/versions`),
  /** `from` 0 is the empty card. Without `from`: from the version you approved (0 if none); without `to`: the current one. */
  diff: (id: string, from?: number, to?: number) => {
    const q = new URLSearchParams();
    if (from !== undefined) q.set("from_version", String(from));
    if (to) q.set("to_version", String(to));
    const qs = q.toString();
    return api<AgentDiffOut>(`/api/v1/agents/${id}/diff${qs ? `?${qs}` : ""}`);
  },
  /** Only the current version: an older one answers 409 `approval_stale`. */
  approve: (id: string, version: number) =>
    api<AgentDetailOut>(`/api/v1/agents/${id}/approve`, { method: "POST", body: { version }, idempotent: true }),
  archive: (id: string) =>
    api<AgentDetailOut>(`/api/v1/agents/${id}/archive`, { method: "POST", idempotent: true }),
  restore: (id: string) =>
    api<AgentDetailOut>(`/api/v1/agents/${id}/restore`, { method: "POST", idempotent: true }),
  /** Where the agent runs and ran, newest first. */
  runs: (id: string) => api<AgentRunOut[]>(`/api/v1/agents/${id}/runs`),
  /** The agent's work items with their steps, newest first. */
  work: (id: string) => api<AgentWorkItem[]>(`/api/v1/agents/${id}/work`),
  /** What people said the agent got wrong, and what came of it (D13). */
  feedback: (id: string) => api<AgentFeedbackOut[]>(`/api/v1/agents/${id}/feedback`),
  /** «Не так»: a person's words about the agent's work, by hand. */
  giveFeedback: (id: string, body: { text: string; item_id?: string | null }) =>
    api<AgentFeedbackOut>(`/api/v1/agents/${id}/feedback`, { method: "POST", body, idempotent: true }),
  /** What the agent produced, how long it took, its tokens and services (D12). */
  stats: (id: string) => api<AgentWorkStats>(`/api/v1/agents/${id}/stats`),
  /** «Запустить» or «Перехватить»: a folder of the approved version with a one-time issue number.
   * Spec 042: a helper's own chat is launched from the helper's own card, by its own id. */
  launch: (id: string, takeover = false, runtime: "claude" | "codex" = "claude") =>
    api<AgentLaunchOut>(`/api/v1/agents/${id}/launch`, { method: "POST", body: { takeover, runtime }, idempotent: true }),
  /** The launch folder's zip, saved under its own name (the API wants the bearer: no bare link). */
  downloadFolder: async (out: Pick<AgentLaunchOut, "folder" | "folder_name">) => {
    const blob = await fetchBlob(out.folder);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = out.folder_name;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },
};

/** Spec 031 A1: the PCs that run agents for the tasker, and the commands given to them. Only
 * the owner controls a PC; the team sees its name, owner and availability. */
export const Devices = {
  list: () => api<DeviceOut[]>("/api/v1/devices"),
  /** The PC that shows this code: what the person confirms. */
  lookup: (code: string) =>
    api<DevicePairLookup>("/api/v1/devices/pair/lookup", { method: "POST", body: { code } }),
  confirm: (pairId: string, name?: string) =>
    api<DeviceOut>(`/api/v1/devices/pair/${pairId}/confirm`, { method: "POST", body: name ? { name } : {} }),
  rename: (id: string, name: string) =>
    api<DeviceOut>(`/api/v1/devices/${id}`, { method: "PATCH", body: { name } }),
  /** «Отключить ПК»: access revoked; with `stop`, its processes for us are asked to stop. */
  revoke: (id: string, stop = true) =>
    api<DeviceOut>(`/api/v1/devices/${id}/revoke`, { method: "POST", body: { stop } }),
  /** «Запустить на ПК»: the approved version, to the person's own PC. A second launch while one
   * holds the agent answers 409 `launch_on_its_way` with that command. */
  launch: (deviceId: string, agentId: string, takeover = false, runtime: "claude" | "codex" = "claude") =>
    api<DeviceCommandOut>(`/api/v1/devices/${deviceId}/commands`, {
      method: "POST",
      body: { kind: "launch", agent_id: agentId, takeover, runtime },
      idempotent: true,
    }),
  commands: (deviceId: string) => api<DeviceCommandOut[]>(`/api/v1/devices/${deviceId}/commands`),
  /** An agent's commands on my PCs for the last day, newest first: the launch that holds it and
   * its stops — found again after the sheet was closed or the page reloaded. */
  agentCommands: (agentId: string) => api<DeviceCommandOut[]>(`/api/v1/agents/${agentId}/device-commands`),
  command: (id: string) => api<DeviceCommandOut>(`/api/v1/device-commands/${id}`),
  /** Until the PC accepts it; after that — `stop`. */
  cancel: (id: string) => api<DeviceCommandOut>(`/api/v1/device-commands/${id}/cancel`, { method: "POST" }),
  /** «Остановить процесс» of a launch — with its run, if it began. The answer is the stop. */
  stop: (id: string) => api<DeviceCommandOut>(`/api/v1/device-commands/${id}/stop`, { method: "POST" }),
  /** The owner's word on an unknown outcome: no process there — the reservation goes. */
  resolve: (id: string) =>
    api<DeviceCommandOut>(`/api/v1/device-commands/${id}/resolve`, { method: "POST", body: { no_process: true } }),
};

/** Spec 027, PR 2: a person stops a run and decides what runs ask. */
export const AgentRuns = {
  stop: (runId: string) =>
    api<AgentRunOut>(`/api/v1/agent-runs/${runId}/stop`, { method: "POST", body: {}, idempotent: true }),
};

export const AgentRequests = {
  mine: () => api<AgentRequestOut[]>("/api/v1/agent-requests"),
  get: (id: string) => api<AgentRequestOut>(`/api/v1/agent-requests/${id}`),
  /** What an agent's proposal would change, as approval shows it (D13). */
  diff: (id: string) => api<ProposalDiffOut>(`/api/v1/agent-requests/${id}/diff`),
  decide: (id: string, body: AgentRequestDecision) =>
    api<AgentRequestOut>(`/api/v1/agent-requests/${id}/decide`, { method: "POST", body, idempotent: true }),
};
